import {
  Component,
  effect,
  HostListener,
  inject,
  OnInit,
  OnDestroy,
  ViewChildren,
  QueryList,
} from '@angular/core';
import { ImpactService } from '../../services/impact.service';
import { ActivatedRoute } from '@angular/router';
import { environment } from '../../../../environments/environment';
import {
  CustomPieChartData,
  ImpactReportData,
} from '../../models/impactReportData';
import { CustomPieChartComponent } from '../graphs/custom-pie-chart/custom-pie-chart.component';
import { PdfExportService } from '../../services/pdf-export.service';
import { TranslocoService } from '@jsverse/transloco';
import { ColorsService } from '../../services/colors.service';
import { ForumService } from '../../../shared/services/forum.service';
import { CurrentUserService } from '../../../shared/services/current-user.service';
import { TripService } from 'src/app/trip-calculator/services/trip.service';

type StateOption = {
  label: string;
  value: string;
};

@Component({
  selector: 'app-impact-report',
  templateUrl: './impact-report.component.html',
  styleUrl: './impact-report.component.scss',
  standalone: false,
})
export class ImpactReportComponent implements OnInit, OnDestroy {
  @ViewChildren(CustomPieChartComponent)
  pieCharts!: QueryList<CustomPieChartComponent>;
  impactService = inject(ImpactService);
  private route = inject(ActivatedRoute);
  public graphService = inject(PdfExportService);
  private translocoService = inject(TranslocoService);
  private colorsService = inject(ColorsService);
  private forumService = inject(ForumService);
  private currentUserService = inject(CurrentUserService);

  // Forum functionality
  isForumAccount = false;
  private forumTimer?: number;
  private tripService = inject(TripService);

  transportDistanceData = this.impactService.transportDistanceData;
  impactReportData?: ImpactReportData;
  showPage = true;
  MIN_DATA_LENGTH_FOR_CHART = 1;
  isPercentage: boolean = true;
  value: string = 'percentage';
  overviewMode: 'detailed' | 'mainVsOnSite' = 'mainVsOnSite';
  removeMainTransport: boolean = false;
  isTransportDistance: boolean = true;
  transportKmChartData?: {
    labels: string[];
    datasets: {
      data: number[];
      backgroundColor: string[];
    }[];
  } = {
    labels: [],
    datasets: [
      {
        data: [],
        backgroundColor: [],
      },
    ],
  };

  overviewChartData?: CustomPieChartData;
  overviewPerTravellerChartData?: CustomPieChartData;

  constructor() {
    effect(() => {
      this.impactReportData = this.impactService.impactReportData();

      const transportData =
        this.impactReportData?.trip?.tripImpact?.transportImpactDetail ?? [];
      this.transportKmChartData = this.getTransportKmChartData(transportData);

      this.updateOverviewCharts();

      // Si les données de trip viennent d'arriver et qu'on est sur un compte forum,
      // essayer de démarrer le timer
      if (this.impactReportData?.trip) {
        this.tryStartForumTimer();
      }
    });
  }

  updateOverviewCharts() {
    if (!this.impactReportData) {
      this.overviewChartData = undefined;
      this.overviewPerTravellerChartData = undefined;
      return;
    }
    if (this.overviewMode === 'detailed') {
      this.overviewChartData = this.impactService.getOverviewDetailed(
        this.impactReportData.trip,
      );
      this.overviewPerTravellerChartData =
        this.impactService.getOverviewPerTravellerDetailed(
          this.impactReportData.trip,
        );
    } else {
      this.overviewChartData = this.impactReportData.overview;
      this.overviewPerTravellerChartData =
        this.impactReportData.overviewPerTraveller;
    }
  }

  getTransportKmChartData(tripImpactDetail: { type: string; km?: number }[]) {
    const labels: string[] = [];
    const data: number[] = [];
    const labelSet = new Set<string>();

    tripImpactDetail.forEach((t) => {
      if (!labelSet.has(t.type)) {
        labels.push(t.type);
        labelSet.add(t.type);
        const totalKm = tripImpactDetail
          .filter((x) => x.type === t.type)
          .reduce((sum, x) => sum + (x.km ?? 0), 0);
        data.push(totalKm);
      }
    });

    const backgroundColor = labels.map((type) =>
      this.colorsService['colorFromType'](
        type,
        this.colorsService.TRANSPORT_COLOR,
      ),
    );

    return {
      labels,
      datasets: [
        {
          data,
          backgroundColor,
        },
      ],
    };
  }

  stateOptions: StateOption[] = [
    {
      label: this.translocoService.translate('impactReport.kgCO2e'),
      value: 'kgCO2e',
    },
    {
      label: this.translocoService.translate('impactReport.percentage'),
      value: 'percentage',
    },
  ];

  onStateChange(value: string) {
    this.isPercentage = value === 'percentage';
  }

  get graphOptions() {
    return this.graphService.graphOptions;
  }

  ngOnInit() {
    const id = this.route.snapshot.paramMap.get('id');
    if (!id) {
      console.error('could not retrieve id');
    } else {
      this.impactService.getImpactReportData(id);
    }

    // Vérifier si c'est un compte forum
    setTimeout(() => {
      this.checkForumAccount();
    }, 500);
  }

  ngOnDestroy() {
    this.clearForumTimer();
  }

  /**
   * Vérifie si le compte actuel est un compte forum
   */
  private checkForumAccount(): void {
    const currentUser = this.currentUserService.currentUser();

    if (currentUser?.company) {
      const companyName = currentUser.company.name?.toLowerCase() || '';
      this.isForumAccount =
        companyName.includes('demo-forum') ||
        companyName.includes('forum-demo');

      if (this.isForumAccount) {
        this.tryStartForumTimer();
      }
    }
  }

  /**
   * Essaie de démarrer le timer forum si les conditions sont réunies
   */
  private tryStartForumTimer(): void {
    if (this.impactReportData?.trip && this.isForumAccount) {
      this.startForumTimer();
    }
  }

  /**
   * Démarre le timer de 3-4 secondes pour l'affichage automatique de la modale
   */
  private startForumTimer(): void {
    this.clearForumTimer(); // S'assurer qu'il n'y a pas de timer en cours

    if (!this.impactReportData?.trip) {
      return;
    }

    const tripName = this.impactReportData.trip.title || 'Voyage calculé';
    const co2 = this.impactReportData.trip.tripImpact?.impactPerTraveller || 0;

    // Timer de 3.5 secondes
    this.forumTimer = window.setTimeout(() => {
      this.forumService.showForumModal(tripName, co2);
    }, 3500);
  }

  /**
   * Efface le timer forum s'il existe
   */
  private clearForumTimer(): void {
    if (this.forumTimer) {
      clearTimeout(this.forumTimer);
      this.forumTimer = undefined;
    }
  }

  /**
   * Ouvre la modale forum manuellement (bouton)
   */
  openForumModal(): void {
    if (!this.impactReportData?.trip) return;

    const tripName = this.impactReportData.trip.title || 'Voyage calculé';
    const co2 = this.impactReportData.trip.tripImpact?.impactPerTraveller || 0;

    this.forumService.showForumModal(tripName, co2);
  }

  setOverviewMode(mode: 'detailed' | 'mainVsOnSite') {
    this.overviewMode = mode;
    this.updateOverviewCharts();
  }

  // Modifie les getters pour retourner simplement la variable :
  get overviewChartDataGetter(): CustomPieChartData | undefined {
    return this.overviewChartData;
  }
  get overviewPerTravellerChartDataGetter(): CustomPieChartData | undefined {
    return this.overviewPerTravellerChartData;
  }

  redirectToCalculator() {
    const id = this.route.snapshot.paramMap.get('id');
    if (!id) {
      console.error('could not retrieve id');
    } else {
      this.tripService.navigateToTripDashboard(id);
    }
  }

  @HostListener('window:resize', ['$event'])
  onResize(): void {}

  getImageUrl(path: string | undefined): string {
    if (!path) return '../../../assets/create_account.jpg';
    return path.startsWith('http') ? path : `${environment.apiUrl}${path}`;
  }

  displayPieChart(pieData: CustomPieChartData | undefined) {
    if (!pieData || !pieData.chartData || !pieData.chartData.labels) {
      return false;
    }
    return pieData.chartData.labels?.length >= this.MIN_DATA_LENGTH_FOR_CHART;
  }
}
